import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import utc from 'dayjs/plugin/utc';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Form, message, Spin } from 'antd';
import { constantFormValues } from './root';
import { Box, Button as MuiButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import SectionCard from 'components/section/SectionCard';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import LeaseBulkUpload from './CustomComponents/LeaseBulkUpload2';
import { useGetQueryV3DeltaQuery } from 'store/features/api/graphSlice';
import LeaseHistoryTable from './CustomComponents/cardSections/History';
import LeaseDetailsShowAll from './CustomComponents/LeaseDetailsShowAll';
import ActionButtons from './CustomComponents/cardSections/ActionButtons';
import LeaseSchedule from './CustomComponents/cardSections/LeaseSchedule';
import AgreementFiles from './CustomComponents/cardSections/AgreementFiles';
import LeaseHistory from './CustomComponents/LeaseUtils/History/LeaseHistory';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import AccountingInformation from './CustomComponents/cardSections/AccountingInformation';
import GeneralLeaseInformation from './CustomComponents/cardSections/GeneralLeaseInformation';
import FinancialTermsConditions from './CustomComponents/cardSections/FinancialTermsConditions';
import IFRS16FieldsModifications from './CustomComponents/cardSections/IFRS16FieldsModifications';
import { hasGlKey, isGrossRentLessThanAdvanceOnEachItem, isSequential, stateUpdater, utcConverter } from './CustomComponents/LeaseUtils/helper';

const leaseDataCache = {};

dayjs.extend(utc);

const AutoPinIcon = () => {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M9 4h6l1 6-3 2v7l-2-2-2 2v-7l-3-2 1-6Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
		</svg>
	);
};

const Create = ({ screen = 'CREATE' }) => {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const { state, pathname } = useLocation();
	const actionButtonsRef = useRef();
	const leaseScheduleRef = useRef();
	const leaseDetailsShowAllRef = useRef();
	const pinStorageKey = `lease-record-management-v9:${screen?.toLowerCase()}:${pathname}`;

	const cacheKey = screen?.toLowerCase() === 'view' ? `${state?._id}-${state?.processCode}` : null;

	const initialCachedData = cacheKey ? leaseDataCache[cacheKey] : null;

	const [tabInstanceId] = useState(() => `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

	const [rowIndex, setRowIndex] = useState(0);
	const [selectedTab, setSelectedTab] = useState(0);
	const [selectHistory, setSelectHistory] = useState(null);
	const [isHeaderPinned, setIsHeaderPinned] = useState(() => sessionStorage.getItem(pinStorageKey) !== '0');
	const [cachedLeaseData, setCachedLeaseData] = useState(initialCachedData);
	const [dataFullyLoaded, setDataFullyLoaded] = useState(!!initialCachedData);
	const [formState, setFormState] = useState({
		submitPayload: null,
		showFormSubmitModal: false,
		showActiveDeactiveModal: false,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});

	const { lynFormProperties, isLoading } = useControlData();

	const updateFormState = stateUpdater(setFormState);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	const [creationApi, { isLoading: isCreatLoading, error: createError, isSuccess: isCreateSuccess, isError: isCreateError }] =
		useGetSingleIdentityMutation();

	const [getSingleLeaseData, { data: singleLeaseData, isLoading: isViewLoading }] = useGetSingleIdentityMutation();

	const displayLeaseData = cachedLeaseData || singleLeaseData;

	const { profileData } = useSelector((state) => state?.profile);

	const statusData = useMemo(() => {
		return screen?.toLowerCase() === 'view'
			? !(
					displayLeaseData?.activationStatus === 'W-I-P' &&
					displayLeaseData?.recordStatus === 'parked' &&
					profileData?.userId === displayLeaseData?.approverUserId[0]
				)
				? displayLeaseData?.data
				: displayLeaseData?.parkedData
			: [];
	}, [displayLeaseData, screen]);

	const hasGlAssigned = hasGlKey(statusData);

	const hasCCAssigned = Boolean(statusData?.refCCAssignmentIdentityId);

	useEffect(() => {
		if (screen?.toLowerCase() === 'create') {
			form.resetFields();

			setTimeout(() => {
				if (initialFormValues && form) {
					form.setFieldsValue(initialFormValues);

					setDataFullyLoaded(true);
				}
			}, 0);
		}

		if (screen?.toLowerCase() === 'view') {
			if (!initialCachedData) {
				const fetchLeaseData = async () => {
					try {
						const response = await getSingleLeaseData({
							suffixCode: '',
							_id: state?._id ?? '',
							operation: 'get-single',
							processCode: state?.processCode,
						}).unwrap();

						leaseDataCache[cacheKey] = response;

						setCachedLeaseData(response);
					} catch (error) {
						console.error('Error fetching lease data:', error);
					}
				};
				fetchLeaseData();
			} else {
				const fetchInBackground = async () => {
					try {
						const response = await getSingleLeaseData({
							suffixCode: '',
							_id: state?._id ?? '',
							operation: 'get-single',
							processCode: state?.processCode,
						}).unwrap();

						if (!isEqual(initialCachedData, response)) {
							leaseDataCache[cacheKey] = response;

							setCachedLeaseData(response);
						}
					} catch (error) {
						// Silent fail for background fetch
					}
				};

				requestAnimationFrame(() => {
					fetchInBackground();
				});
			}
		}
	}, [screen, initialCachedData, state?._id, state?.processCode, cacheKey]);

	useEffect(() => {
		if (screen === 'CREATE') {
			if (isCreateSuccess) {
				toast.success('Success! Action completed ', toastConfig);

				navigate(-1);
			}
			if (isCreateError) {
				if (createError?.data?.code === '501') {
					toast.error(createError?.data?.message, toastConfig);
				} else {
					toast.error('Failed! Action not completed', toastConfig);
				}
			}
		}
	}, [isCreateSuccess, createError, isCreateError, screen]);

	const initialFormValues = useMemo(
		() => ({
			tabIndex: '1',
			version: '',
			leaseId: '',
			leaseTitle: '',
			leaseType: 'Finance Lease',
			lessorName: '',
			lessorID: null,
			leaseTerm: '',
			leaseEndDate: '',
			creationDate: dayjs(),
			lastModifiedDate: dayjs(),
			escalationPercentage: '0',
			inputChangeMonth: '1',
			grossRentList: [
				{
					from: 1,
					to: '',
					amount: '',
					remarks: '',
				},
			],
			grossRentListSet: [[]],
			advanceAmountListSet: [[]],

			advanceAmountList: [
				{
					from: 1,
					to: '',
					amount: '',
					totalAmount: '',
				},
			],
			advanceAmount: '0',
			advanceDuration: '0',
			upfrontLeasePayment: '',
			initialDirectCost: '',
			securityDeposit: '',
			initialDiscountRate: '0',
			// rightOfUseAssetValue: '10000250 - RoU Front Offices',
			// glleaseLiability: '20001120 - Lease Liabilities',
			// costCenterInterestExpense: '902001 - Finance Common',
			// glInterestExpense: '40004250 - Interest for Lease',
			// leasePayableControlAccount: '20001100 - Lease Payable',
			// glAdvanceRent: '10002050 - Advance Rent',
			// depreciationRoUCostCenter: '902003 - Finance Common',
			// depreciationRoU: '20007555 - Depreciation RoU',
			// accumDepreciationRoU: '20004255 - Accum Depreciation RoU',
			// glBankAccount: '10003001 - SCB Main',
			// leaseExpense: '40004260 - Interest for Lease',
			// leaseExpenseCostCenter: '902002 - Finance Common',
			// securityDepositHead: '10009008 - Security Deposite',
			// accountsPayableHead: '20003300 - Accounts Payable',
			identityId: '',
			cashIncentiveAmount: '',

			paymentModality: 'Start',
			paymentFrequency: 'Monthly',
			endOfFinancialYear: utcConverter(dayjs().month(11).date(31)),
			changes: [
				{
					inputChangeEffectiveDate: '',
					inputChangeAdvance: '',
					inputChangeAdvanceAmounts: [],
					inputChangeDescription: 'Initial Rent',
					inputChangeDiscountRate: 0,
					inputChangeEscalation: 0,
					inputChangeGrossAmount: 0,
					inputChangeGrossRents: [],
					inputChangeLeaseTerm: '',
					paymentFrequency: 'Monthly',
					paymentTime: 'Start',
				},
			],
			agreementFiles: [
				{
					id: '',
					name: '',
					file: '',
				},
			],
		}),
		[]
	);

	useEffect(() => {
		if (screen?.toLowerCase() === 'view' && statusData) {
			// let modifiedLeaseTerm;
			// if (statusData?.paymentFrequency === 'Monthly') {
			// 	modifiedLeaseTerm = statusData?.leaseTerm;
			// } else if (statusData?.paymentFrequency === 'Quarterly') {
			// 	modifiedLeaseTerm = statusData?.leaseTerm / 3;
			// }
			const viewValues = {
				// General Lease Information section
				tabIndex: '1',
				modificationSequence: statusData?.modificationSequence,
				leaseId: statusData?.leaseId,
				leaseType: statusData?.leaseType,
				leaseTitle: statusData?.leaseTitle,
				lessorName: statusData?.lessorName,
				lessorID: statusData?.lessorID,
				paymentFrequency: statusData?.paymentFrequency,
				paymentModality: statusData?.paymentModality,
				creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,
				lastModifiedDate: dayjs(statusData?.lastModifiedDate),

				// Financial Terms & Conditions section
				advanceAmount: statusData?.advanceAmount,
				advanceDuration: statusData?.advanceDuration,
				grossRent: statusData?.grossRent,
				leaseTerm: statusData?.leaseTerm,
				leaseStartDate: dayjs(statusData?.leaseStartDate),
				leaseEndDate: dayjs(statusData?.leaseEndDate),
				escalationPercentage: statusData?.escalationPercentage,
				initialDiscountRate: statusData?.initialDiscountRate,
				upfrontLeasePayment: statusData?.upfrontLeasePayment ?? 0,
				initialDirectCost: statusData?.initialDirectCost ?? 0,
				securityDeposit: statusData?.securityDeposit ?? 0,
				endOfFinancialYear: utcConverter(dayjs().month(11).date(31)),
				// Accounting Information section
				rightOfUseAssetValue: statusData?.rightOfUseAssetValue,
				glleaseLiability: statusData?.glleaseLiability ?? '',
				costCenterInterestExpense: statusData?.costCenterInterestExpense,
				glInterestExpense: statusData?.glInterestExpense,
				leaseExpenseCostCenter: statusData?.leaseExpenseCostCenter,
				leaseExpense: statusData?.leaseExpense,
				leasePayableControlAccount: statusData?.leasePayableControlAccount,
				glAdvanceRent: statusData?.glAdvanceRent,
				depreciationRoUCostCenter: statusData?.depreciationRoUCostCenter,
				depreciationROU: statusData?.depreciationRoU,
				accumDepreciationROU: statusData?.accumDepreciationRoU,
				glBankAccount: statusData?.glBankAccount,
				securityDepositHead: statusData?.securityDepositHead,
				accountsPayableHead: statusData?.accountsPayableHead,
				effectiveDateHistory: statusData?.effectiveDateHistory,
				cashIncentiveAmount: statusData?.cashIncentiveAmount || statusData?.cashIncentive,
				//others
				version: String(displayLeaseData?.version),
				leaseLiability: statusData?.leaseLiability,
				amortizationSchedule: statusData?.amortizationSchedule,
				leaseCategory: statusData?.leaseCategory,
				leaseName: statusData?.leaseName,
				contactDetails: statusData?.contactDetails,
				identityId: displayLeaseData?._id,

				renewalOptions: statusData?.renewalOptions,
				terminationOption: statusData?.terminationOption,
				month: statusData?.month,
				refundability: statusData?.refundability,
				agreementFiles: statusData?.agreementFiles,

				responsibility: statusData?.responsibility,
				basisOfRate: statusData?.basisOfRate,
				nonLeaseComponents: statusData?.nonLeaseComponents,
				separateServiceCharges: statusData?.separateServiceCharges,
				modificationDetails: statusData?.modificationDetails,
				discountRateChanges: statusData?.discountRateChanges,
				advanceAmounts: statusData?.advanceAmounts,
				grossRents: statusData?.grossRents,
				grossRentList: statusData?.grossRentList,
				grossRentListSet: statusData?.grossRentListSet,
				advanceAmountListSet: statusData?.advanceAmountListSet,
				advanceAmountList: statusData?.advanceAmountList,

				//changes
				changes: statusData?.changes?.map((item) => {
					const { inputChangeEffectiveDate, ...change } = item;
					return {
						...change,
						inputChangeEffectiveDate: dayjs(inputChangeEffectiveDate),
					};
				}),
			};
			// console.log('🚀 ~ changes:statusData?.changes?.map ~ statusData:', statusData);

			setRowIndex(statusData?.changes?.length);

			// Set form values synchronously for immediate display
			form.setFieldsValue(viewValues);

			// Always set dataFullyLoaded to true after form is populated
			// Use requestAnimationFrame to ensure it happens after render
			requestAnimationFrame(() => {
				// console.log('Setting dataFullyLoaded to true for view mode');
				setDataFullyLoaded(true);
			});
		}
	}, [screen, statusData, displayLeaseData]);

	const handleSubmit = async () => {
		try {
			await form.validateFields();

			const formData = form.getFieldsValue();

			// console.log('🚀 ~ handleSubmit ~ formData:', formData);

			if (formData?.paymentFrequency === 'Quarterly' && formData?.leaseTerm % 3 !== 0) {
				message.error(
					"Total lease months must be a multiple of 3 for quarterly payments. Please correct the Lease Payment Schedule's To Month field"
				);
				return;
			}
			if (!isSequential(formData?.grossRentList)) {
				message.error('Gross Rent List must be sequential');
				return;
			}
			if (!isSequential(formData?.advanceAmountList)) {
				message.error('Advance Amount List must be sequential');
				return;
			}
			let grossRentListSet = [];
			let advanceAmountListSet = [];
			let effectiveDateHistory = [];
			grossRentListSet?.push(formData?.grossRentList);
			// console.log(grossRentListSet, 'formData gr');

			advanceAmountListSet?.push(formData?.advanceAmountList);
			effectiveDateHistory?.push(utcConverter(formData?.leaseStartDate));

			const isGrossRentAndAdvanceAmountValid = isGrossRentLessThanAdvanceOnEachItem(formData?.grossRents, formData?.advanceAmounts);

			if (!isGrossRentAndAdvanceAmountValid) {
				message.error('Inconsistency between Lease Payment and Advance Adjustment');
				return;
			}
			const createPagePayload = {
				modificationSequence: 0,
				leaseStartDate: utcConverter(formData?.leaseStartDate),
				leaseTitle: formData?.leaseTitle,
				lessorName: formData?.lessorName,
				lessorID: formData?.lessorID,
				leaseTerm: formData?.leaseTerm,
				leaseEndDate: utcConverter(formData?.leaseEndDate),
				paymentModality: formData?.paymentModality,
				leaseType: formData?.leaseType,
				initialDiscountRate: formData?.initialDiscountRate,
				grossRentList: formData?.grossRentList,
				paymentFrequency: formData?.paymentFrequency,
				advanceAmountList: formData?.advanceAmountList,
				advanceAmount: formData?.advanceAmount,
				advanceAmounts: formData?.advanceAmounts,
				upfrontLeasePayment: formData?.upfrontLeasePayment ?? 0,
				initialDirectCost: formData?.initialDirectCost ?? 0,
				securityDeposit: formData?.securityDeposit ?? 0,
				grossRents: formData?.grossRents,
				cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
				grossRent: 0,
				escalationPercentage: 0,
				grossRentListSet,
				advanceAmountListSet,
				effectiveDateHistory,
				modifyCount: 0,
				editCount: 0,
				changes: [
					{
						paymentFrequency: formData?.changes?.[0]?.paymentFrequency,
						paymentTime: formData?.changes?.[0]?.paymentTime,
						inputChangeLeaseTerm: formData?.changes?.[0]?.inputChangeLeaseTerm,
						inputChangeGrossAmount: formData?.changes?.[0]?.inputChangeGrossAmount ?? 0,
						inputChangeDiscountRate: formData?.changes?.[0]?.inputChangeDiscountRate,
						inputChangeEscalation: formData?.changes?.[0]?.inputChangeEscalation ?? 0,
						inputChangeAdvance: formData?.changes?.[0]?.inputChangeAdvance,
						inputChangeEffectiveDate: formData?.changes?.[0]?.inputChangeEffectiveDate,
						inputChangeAdvanceAmounts: formData?.advanceAmounts,
						inputChangeGrossRents: formData?.grossRents,
						inputChangeDescription: formData?.changes?.[0]?.inputChangeDescription,
						upfrontLeasePayment: formData?.upfrontLeasePayment ?? 0,
						initialDirectCost: formData?.initialDirectCost ?? 0,
						securityDeposit: formData?.securityDeposit ?? 0,
						cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
					},
				],
				modifiedDateTime: utcConverter(dayjs()),
				paymentModalityLabel: constantFormValues?.paymentModality?.options?.find((item) => item.value === formData?.paymentModality)?.label,

				lastModifiedDate: utcConverter(dayjs()),

				// grossRentListSet,
				// advanceAmountListSet,
				advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0).length,
			};

			updateFormState('showFormSubmitModal', true);
			updateFormState('modalData.head', 'Confirmation');
			updateFormState('modalData.label', 'Do you want to create?');

			updateFormState('submitPayload', createPagePayload);
		} catch (error) {
			console.error('Validation Failed:', error);
			toast.error('Please fill all required fields', toastConfig);
		}
	};

	useEffect(() => {
		if (displayLeaseData && displayLeaseData.activationStatus !== undefined) {
			window.leaseActivationStatus = displayLeaseData.activationStatus;
		}
	}, [displayLeaseData]);

	useEffect(() => {
		setIsHeaderPinned(sessionStorage.getItem(pinStorageKey) !== '0');
	}, [pinStorageKey]);

	const handleModalSubmit = async () => {
		await creationApi({
			operation: 'add-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: formState?.submitPayload,
		});
	};

	const handleAutoPinToggle = () => {
		const nextState = !isHeaderPinned;
		setIsHeaderPinned(nextState);
		sessionStorage.setItem(pinStorageKey, nextState ? '1' : '0');
	};

	const selectTabHandler = (event, newValue) => {
		if (isCreatLoading) return;

		setSelectedTab(newValue);
		if (newValue === 1 && dataFullyLoaded && screen?.toLowerCase() !== 'create') {
			if (leaseScheduleRef.current?.queryV3Loader) {
				// leaseScheduleRef.current.queryV3Loader();
			}
		}

		if (newValue === 4 && dataFullyLoaded && screen?.toLowerCase() === 'view') {
			if (leaseDetailsShowAllRef.current?.loadData) {
				leaseDetailsShowAllRef.current.loadData();
			}
		}
	};

	useEffect(() => {
		if (dataFullyLoaded && screen?.toLowerCase() === 'view') {
			// actionButtonsRef.current.handleDataLoading();
		}
	}, [dataFullyLoaded]);

	const showSpinner = screen?.toLowerCase() === 'view' ? isViewLoading && !initialCachedData : isCreatLoading;

	const tabItem = [
		{
			key: '1',
			label: 'Lease Details',
			children: (
				<>
					<SectionCard sx={{ padding: '16px', borderRadius: '10px', border: '1px solid #e6ebf2', marginBottom: '24px' }}>
						<Typography
							variant="h4"
							sx={{ color: '#344054', padding: '0 0 12px 0', fontWeight: 600, fontSize: '18px', borderBottom: '1px solid #eaeef4' }}
						>
							Basic Information
						</Typography>
						<GeneralLeaseInformation screen={screen} vendorList={lynFormProperties?.vendorList} />
					</SectionCard>

					<FinancialTermsConditions screen={screen} dataFullyLoaded={dataFullyLoaded} />

					<SectionCard sx={{ padding: '16px', borderRadius: '10px', border: '1px solid #e6ebf2', marginBottom: '24px' }}>
						<Typography
							variant="h4"
							sx={{ color: '#344054', padding: '0 0 12px 0', fontWeight: 600, fontSize: '18px', borderBottom: '1px solid #eaeef4' }}
						>
							IFRS - 16 Specific Fields
						</Typography>
						<IFRS16FieldsModifications screen={screen} rowIndex={rowIndex} />
					</SectionCard>

					<ActionButtons
						screen={screen}
						viewData={singleLeaseData}
						selectHistory={setSelectHistory}
						dataFullyLoaded={dataFullyLoaded}
						ref={actionButtonsRef}
						handleCreation={handleSubmit}
						hasGlAssigned={hasGlAssigned}
						hasCCAssigned={hasCCAssigned}
					/>
				</>
			) || <></>,
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: (
				<LeaseSchedule
					screen={screen}
					viewData={singleLeaseData}
					dataFullyLoaded={dataFullyLoaded}
					ref={leaseScheduleRef}
					hasGlAssigned={hasGlAssigned}
					hasCCAssigned={hasCCAssigned}
					key={`lease-schedule-${singleLeaseData?.data?.leaseId || 'new'}-${tabInstanceId}`}
				/>
			),
		},
		...(screen?.toLowerCase() === 'view'
			? [
					{
						key: '3',
						label: 'Modification History',
						children: (
							<>
								<LeaseHistory viewData={singleLeaseData} selectHistory={setSelectHistory} />
							</>
						),
					},
				]
			: []),
		...(screen?.toLowerCase() !== 'create'
			? [
					{
						key: screen?.toLowerCase() === 'view' ? '4' : '3',
						label: 'Accounts Assignment',
						children: <AccountingInformation screen={screen} leaseData={displayLeaseData} identityId={singleLeaseData?._id} form={form} />,
					},
				]
			: []),
		...(screen?.toLowerCase() === 'view'
			? [
					{
						label: 'General Ledgers',
						key: screen?.toLowerCase() === 'view' ? '5' : '',
						children: <LeaseDetailsShowAll ref={leaseDetailsShowAllRef} />,
					},
					{
						key: screen?.toLowerCase() === 'view' && '8',
						label: 'History',
						children: <LeaseHistoryTable />,
					},
				]
			: []),
		...(screen?.toLowerCase() !== 'create' && statusData?.agreementFiles?.length > 0
			? [
					{
						label: 'Agreements',
						key: screen?.toLowerCase() === 'view' ? '6' : '4',
						children: <AgreementFiles screen={screen} />,
					},
				]
			: []),
		...(screen?.toLowerCase() !== 'view'
			? [
					{
						key: screen?.toLowerCase() === 'view' ? '7' : '5',
						label: 'Bulk Upload',
						children: <LeaseBulkUpload vendorList={lynFormProperties?.vendorList} />,
					},
				]
			: []),
	];

	return (
		<Box>
			<Spin spinning={showSpinner} style={{ maxHeight: '100%' }}>
				<Box
					sx={{
						mb: 2,
						backgroundColor: isHeaderPinned ? 'background.main' : 'transparent',
						top: isHeaderPinned ? '60px' : 'auto',
						zIndex: isHeaderPinned ? 20 : 'auto',
						position: isHeaderPinned ? 'sticky' : 'static',
						px: { xs: 2, md: 2.5 },
						pt: { xs: 1.75, md: 2 },
						bgcolor: '#fff',
						border: '1px solid #e6ebf2',
						borderRadius: '10px',
						boxShadow: 'none',
					}}
				>
					<Stack
						direction={{ xs: 'column', md: 'row' }}
						alignItems={{ xs: 'flex-start', md: 'center' }}
						justifyContent="space-between"
						spacing={2}
						sx={{ mb: 1.25 }}
					>
						<Box>
							<Typography sx={{ color: '#1f2937', fontSize: '18px', fontWeight: 600, lineHeight: 1.2 }}>
								{screen === 'CREATE' ? `Create ${state?.processName}` : `View ${state?.processName}`}
							</Typography>
						</Box>

						<MuiButton
							onClick={handleAutoPinToggle}
							startIcon={<AutoPinIcon />}
							sx={{
								px: 2,
								fontSize: '15px',
								fontWeight: 500,
								height: '34px',
								minHeight: '34px',
								borderRadius: '8px',
								textTransform: 'none',
								boxShadow: 'none',
								color: isHeaderPinned ? '#1D4ED8' : '#344054',
								border: `1px solid ${isHeaderPinned ? '#BFDBFE' : '#d0d5dd'}`,
								backgroundColor: isHeaderPinned ? '#EFF6FF' : '#FFFFFF',
								'&:hover': {
									boxShadow: 'none',
									backgroundColor: isHeaderPinned ? '#DBEAFE' : '#F9FAFB',
								},
							}}
						>
							{isHeaderPinned ? 'Pinned' : 'Auto'}
						</MuiButton>
					</Stack>

					<Box
						sx={{
							px: { xs: 0.25, md: 0.5 },
							borderTop: '1px solid #eef2f7',
						}}
					>
						<Tabs
							value={selectedTab}
							onChange={selectTabHandler}
							variant="scrollable"
							scrollButtons="auto"
							allowScrollButtonsMobile
							sx={{
								minHeight: '36px',
								'& .MuiTabs-indicator': {
									height: '2px',
									backgroundColor: '#2563eb',
									borderRadius: '999px',
								},
								'& .MuiTab-root': {
									minHeight: '36px',
									px: { xs: 1.25, md: 1.5 },
									mr: { xs: 0.5, md: 1 },
									textTransform: 'none',
									fontSize: '14px',
									fontWeight: 500,
									color: '#667085',
								},
								'& .Mui-selected': {
									color: '#2563eb !important',
									fontWeight: 600,
								},
							}}
						>
							{tabItem?.map((item) => (
								<Tab key={item.key} label={item.label} />
							))}
						</Tabs>
					</Box>
				</Box>

				<Form form={form} layout="vertical" autoComplete="off" requiredMark={false} onFinish={handleSubmit} initialValues={initialFormValues}>
					<AntdValuePreview form={form} show={false} />

					<Box>
						{tabItem?.map((item, index) => (
							<Box
								key={index}
								style={{
									display: selectedTab === index ? 'block' : 'none',
								}}
							>
								{index === 0 ? item.children : <SectionCard>{item.children}</SectionCard>}
							</Box>
						))}
					</Box>
				</Form>

				{formState?.showFormSubmitModal && (
					<ComptrollerConfirmModal
						modalData={formState?.modalData}
						handleSubmit={handleModalSubmit}
						setShowModal={setShowFormSubmitModal}
						showModal={formState?.showFormSubmitModal}
					/>
				)}

				{selectHistory && (
					<IdentityCaseActivityModal
						title={state?.processName}
						selectItem={selectHistory}
						setSelectHistory={setSelectHistory}
						processCode={singleLeaseData?.processCode}
						processVersion={singleLeaseData?.parkProcessVersion}
					/>
				)}
			</Spin>
		</Box>
	);
};

export default Create;

export const useControlData = () => {
	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

	const {
		data: getVendorList,
		isLoading: isVendorLoading,
		isError: isVendorError,
	} = useGetQueryV3DeltaQuery(
		{
			companyCode,
			fieldCodes: ['companyCode'],
			fieldValues: [companyCode],
			name: 'getVendorData',
		},
		{
			skip: !companyCode,
		}
	);

	const lynFormProperties = useMemo(
		() => ({
			vendorList:
				getVendorList?.results?.map((item) => ({
					label: item?.vendorName,
					value: item?._id,
					code: item?.vendorCode,
				})) ?? [],
		}),
		[getVendorList]
	);

	return {
		lynFormProperties,
		isLoading: isVendorLoading,
		isError: isVendorError,
	};
};
